import type React from 'react';

import { SocialSharing, SocialSharingGroup, Text } from '@change/ds-core-ui';
import type { Icon } from '@change/ds-icons';
import { EmailIcon, FacebookIcon, ShareNowIcon, TwitterIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';

import type { Channel } from './hooks/useShareLinks';
import { useShareLinks } from './hooks/useShareLinks';

const ICON_MAP: Record<Channel, React.FC<Icon>> = {
	twitter: TwitterIcon,
	facebook: FacebookIcon,
	email: EmailIcon,
};

const VARIANT_MAP: Record<Channel, 'twitter' | 'facebook' | 'default'> = {
	twitter: 'twitter',
	facebook: 'facebook',
	email: 'default',
};

const CHANNELS = new Set<string>(Object.keys(ICON_MAP));

export const ShareLinks: React.FC = () => {
	const {
		data: { shareActions, isLoading },
	} = useShareLinks();

	if (isLoading) return null;

	return (
		<div className="flex items-center gap-4">
			<Text className="text-typography-weak hidden uppercase md:block" size="small">
				<Translate value="fe.components.news_header.share" />
			</Text>
			<Text className="text-typography-weak flex items-center uppercase md:hidden" size="small">
				<Text className="sr-only" size="small">
					<Translate value="fe.components.news_header.share" />
				</Text>
				<ShareNowIcon size="medium" />
			</Text>

			<SocialSharingGroup layout="inline" className="gap-4">
				{shareActions
					.filter(({ channel }) => CHANNELS.has(channel))
					.map(({ channel, ShareAction }) => {
						const ch = channel as Channel;
						const IconComponent = ICON_MAP[ch];
						return (
							<ShareAction key={channel}>
								{/* TODO aria-label should be a i18n string, at least for email */}
								<SocialSharing
									data-testid={`news-share-${channel}`}
									data-qa={`news-share-${channel}`}
									aria-label={channel}
									icon={<IconComponent />}
									label=""
									variant={VARIANT_MAP[ch]}
									size="medium"
									className="w-8"
								/>
							</ShareAction>
						);
					})}
			</SocialSharingGroup>
		</div>
	);
};
